import { useNavigate, useSearchParams } from 'react-router-dom';
import Dica from '@/components/feature/Dica';
import type { PerfilEvento } from '@/lib/perfilEvento';
import TelaTitulo from './TelaTitulo';
import HubMais from './mais/HubMais';
import type { TelaMais } from './mais/HubMais';
import BlocoAvisos from './mais/BlocoAvisos';
import BlocoDirecao from './mais/BlocoDirecao';
import BlocoRecursos from './mais/BlocoRecursos';
import BlocoHistorico from './mais/BlocoHistorico';
import AbaSorteio from './AbaSorteio';
import DeslizarEntrada from '@/components/movimento/DeslizarEntrada';

interface Props {
  eventoId: number;
  perfil: PerfilEvento | null;
  /** Existe aba Equipes? (mantido para compatibilidade de chamada) */
  mostrarEquipes?: boolean;
  /** A ação entre amigos entra no hub só quando está ligada. */
  mostrarRifa?: boolean;
  aoMudouRecurso: () => void;
}

const TELAS: TelaMais[] = ['sorteio', 'avisos', 'direcao', 'recursos', 'historico'];

const TITULO: Record<TelaMais, string> = {
  sorteio: 'Ação entre amigos',
  avisos: 'Avisos no celular',
  direcao: 'Quem está na direção',
  recursos: 'Recursos ligados',
  historico: 'Histórico',
};

const DICA: Record<TelaMais, string> = {
  sorteio: 'painel.sorteio',
  avisos: 'painel.ajustes',
  direcao: 'painel.ajustes',
  recursos: 'painel.ajustes',
  historico: 'painel.ajustes',
};

// Aba "Mais": hub de cartões. Cada cartão abre uma tela cheia (?aba=mais&tela=...).
export default function AbaAjustes({
  eventoId,
  perfil,
  mostrarRifa = false,
  aoMudouRecurso,
}: Props) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const telaDaUrl = searchParams.get('tela');
  const tela: TelaMais | null =
    telaDaUrl && TELAS.includes(telaDaUrl as TelaMais) ? (telaDaUrl as TelaMais) : null;

  function abrirTela(destino: TelaMais) {
    const next = new URLSearchParams(searchParams);
    next.set('aba', 'mais');
    next.set('tela', destino);
    next.delete('sub');
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function voltarAoHub() {
    const next = new URLSearchParams(searchParams);
    next.delete('tela');
    setSearchParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  if (!tela) {
    return (
      <DeslizarEntrada className="space-y-4">
        <div className="flex items-center gap-2 px-1">
          <h2 className="text-[17px] font-bold text-foreground-950">Mais</h2>
          <Dica id="painel.ajustes" eventoId={eventoId} />
        </div>

        <HubMais
          temRifa={mostrarRifa}
          vendeFichas={perfil?.chaves.entrada_franca === true}
          mostrarCriancas={perfil?.chaves.usa_equipes === true}
          aoAbrir={abrirTela}
          aoEditar={() => navigate(`/evento/${eventoId}/editar`)}
          aoAbrirCriancas={() => navigate(`/gerenciar/${eventoId}/criancas`)}
        />

        <div className="rounded-3xl bg-danger-50 p-5">
          <h2 className="text-[16px] font-bold text-danger-800">Encerrar ou tirar do ar</h2>
          <p className="mt-1 text-[14px] text-danger-700">
            Precisa encerrar o evento ou tirar do ar? Fale com o Se Liga — a gente cuida disso com
            segurança.
          </p>
        </div>
      </DeslizarEntrada>
    );
  }

  return (
    <DeslizarEntrada>
      <TelaTitulo
        titulo={TITULO[tela]}
        aoVoltar={voltarAoHub}
        dicaId={DICA[tela]}
        eventoId={eventoId}
      />

      {tela === 'avisos' && <BlocoAvisos eventoId={eventoId} />}
      {tela === 'direcao' && <BlocoDirecao eventoId={eventoId} />}
      {tela === 'recursos' && (
        <BlocoRecursos
          eventoId={eventoId}
          perfil={perfil}
          aoMudouRecurso={aoMudouRecurso}
        />
      )}
      {tela === 'historico' && <BlocoHistorico eventoId={eventoId} />}
      {tela === 'sorteio' && <AbaSorteio eventoId={eventoId} />}
    </DeslizarEntrada>
  );
}